import type { InsightReportType } from "@/types/insight";
import { isObjectTruthy } from "./object";
import {
  capitalizeFirstLetter,
  cleanStringExcelSheet,
  snakeToTitleCase,
} from "./string";
import { utils, write } from "xlsx";
import { saveAs } from "file-saver";
import { CellObject } from "xlsx";
import { isArrayWithItems } from "./array";

export const orderedUnitsArray = [
  "percentage",
  "index",
  "market_size",
  "audience_sample_size",
  "weighted_response_count",
];
type ExcelDataType = "b" | "e" | "n" | "d" | "s" | "z";

// Primary Function to export XLSX of an Insight Report
export const exportInsightXLSX = async ({
  reportData,
  exportType = "standard-single", // "standard-single" | "standard-multiple"
  name, // Optional name for the export file
}: {
  reportData: InsightReportType | InsightReportType[]; // Single report or array of reports for multiple export
  exportType?: "standard-single" | "standard-multiple";
  name?: string; // Optional name for the export file
}) => {
  try {
    /**
     * Ref VI-2848
     * Commented out other units to exclude them from export. Only interested in percentage and audience_sample_size at the time of writing.
     * Patrick Fortaleza
     */
    const units = {
      // weighted_response_count: null,
      audience_sample_size: null,
      // market_size: "M",
      percentage: "%",
      // index: null,
    };
    // Validation Start
    if (!reportData || !isObjectTruthy(reportData)) {
      throw new Error("Invalid report data provided for export.");
    }
    // Prepare the export
    const fileType =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;";
    const wb = utils.book_new();
    const cachedSheetNames: string[] = [];

    // Appends data to a tab in the file
    const appendDataToTab = (
      output: any,
      reportData: InsightReportType,
      index?: number
    ) => {
      const data = appendReportDataToTableArray({
        exportType: "rankers",
        output,
        variable: reportData,
        selectedMonths: reportData.timeframe,
        audienceDefinition: reportData.audience_definition,
      });
      let sheetName = index
        ? `Audience ${index}`
        : cleanStringExcelSheet(reportData.name);

      if (sheetName.length >= 25) {
        sheetName = `${sheetName.substring(0, 25)}...`;
      }

      let nameCounter = 0;

      while (cachedSheetNames.includes(sheetName.toLowerCase())) {
        nameCounter += 1;
        sheetName = sheetName.replace(/[(0-9)]/g, "");
        sheetName = `${sheetName}(${nameCounter})`;
      }

      cachedSheetNames.push(sheetName.toLowerCase());
      const ws = utils.aoa_to_sheet(data);
      utils.book_append_sheet(wb, ws, sheetName);
    };

    // Single Standard Export
    // Make one single tab with all the data of a single report/audience.
    if (exportType === "standard-single") {
      // Append the output in the workbook
      if (isArrayWithItems(reportData)) {
        throw new Error("Invalid report data for single export.");
      }
      const singleReport = reportData as InsightReportType;

      const output = exportStandard(singleReport, units);
      appendDataToTab(output, singleReport);
    }
    // Multiple Standard Export
    if (exportType === "standard-multiple") {
      // Make one tab for each audience in the report from an array of InsightReportType
      if (!isArrayWithItems(reportData)) {
        throw new Error("Invalid report data for multiple export.");
      }
      reportData.forEach((report: InsightReportType, i) => {
        const output = exportStandard(report, units);
        appendDataToTab(output, report, i + 1);
      });
    }

    // Finalize the export
    const excelBuffer = write(wb, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: fileType });

    saveAs(data, name ? `${name} Export.xlsx` : `Insight Export.xlsx`);
  } catch (error: any) {
    console.log(error);
  }
};

// Supporting Functions to export XLSX of an Insight Report
export const exportStandard = (variable: any, units: any) => {
  const output = formatTableArrayOfArrays({
    responses: variable.levels,
    units,
    variable,
    exportType: "rankers",
  });

  if (!output) {
    throw new Error("Error formatting table " + variable.name);
  }

  return output;
};

export const appendReportDataToTableArray = (
  //   {
  //   exportType = "standard",
  //   output,
  //   variable,
  //   selectedMonths = [],
  //   selectedCountries = [],
  //   audienceDefinition = {},
  // }
  {
    exportType,
    output,
    variable,
    selectedMonths,
    audienceDefinition,
    selectedCountries,
  }: {
    exportType: "standard" | "rankers";
    output: any;
    variable: InsightReportType;
    selectedMonths: string[]; // ✅ Fix this line
    audienceDefinition: any;
    selectedCountries?: string[];
  }
) => {
  try {
    if (!Array.isArray(output))
      throw new Error(
        "Malformed parameter passed to appendReportDataToTableArray"
      );
    const table = [...output];

    // Push report metadata..

    table.push([]);
    table.push([]);

    // Add Variable Hierarchy
    if (exportType === "standard" || exportType === "rankers") {
      if (Array.isArray(variable.hierarchy)) {
        table.push(["Variable Hierarchy"]);
        table.push([variable.hierarchy.map((h) => h.label).join(" ▸ ")]);

        // new lines...
        table.push([]);
      }
    }

    if (exportType === "rankers" && isObjectTruthy(audienceDefinition)) {
      // Insert audience definition
      // Take the keys and values of the definition (expected strings with array of string values)
      table.push(["Audience Definition"]);
      for (const audienceKey in audienceDefinition) {
        // Add a subheading
        table.push([capitalizeFirstLetter(audienceKey)]);

        // Group the audience definition objects by variable_name and level_name
        const groupedDefinitions = audienceDefinition[audienceKey].reduce(
          (acc: any, currentValue: any) => {
            const key = currentValue["variable_name"];
            if (!acc[key]) {
              acc[key] = [];
            }
            acc[key].push(currentValue.level_name);
            return acc;
          },
          {}
        );

        for (const [variableName, levelNames] of Object.entries(
          groupedDefinitions
        )) {
          table.push([
            `${variableName} ▸ ${(levelNames as string[]).join(", ")}`,
          ]);
        }

        table.push([]);
      }
      // new lines...
      table.push([]);
    }

    // Add Dataset Dates
    if (Array.isArray(selectedMonths) && selectedMonths.length > 0) {
      table.push(["Dataset Dates"]);
      table.push([selectedMonths.join(", ")]);

      // new lines...
      table.push([]);
    }

    // Add Dataset Countries
    if (Array.isArray(selectedCountries) && selectedCountries.length > 0) {
      table.push(["Dataset Countries"]);
      table.push([selectedCountries.join(", ")]);

      // new lines...
      table.push([]);
    }

    // Add Report Date
    table.push([`© ${new Date().getFullYear()} Vision Insights.`]);

    return table;
  } catch (error) {
    console.log(error);
    return output;
  }
};

export const formatTableArrayOfArrays = ({
  responses,
  units,
  variable,
  exportType = "standard",
}) => {
  try {
    const tableArray: string[][] = [];

    let unitsArray = Object.keys(units);

    // Filter out the units of orderedUnitsArray that are not in unitsArray to maintain the desired order
    unitsArray = orderedUnitsArray.filter((u) => unitsArray.includes(u));

    unitsArray.forEach((u) => {
      try {
        // Generate table heading
        tableArray.push(
          [" "].concat([`${variable.name} - [${formatReportMetricName(u)}]`])
        );

        // Generate matrix headings
        if (exportType === "standard") {
          tableArray.push(
            [" "].concat(
              responses[0].audiences.map((response) => response.name)
            )
          );
        }

        const rows = responses.map(({ name, audiences }) => {
          const responseRow: { t: string; v: any }[] = [];
          // Push column heading
          responseRow.push({ t: "s", v: name });

          audiences.forEach((audience) => {
            const value = audience.count[u];
            const cell = formatMetricValue(value, u);

            responseRow.push(cell);
          });

          return responseRow;
        });

        tableArray.push(...rows);
        tableArray.push([" "]);
      } catch (error) {
        console.log(error);
      }
    });

    return tableArray;
  } catch (error) {
    console.log(error);
    return null;
  }
};

const formatReportMetricName = (unit: any) => {
  try {
    switch (unit) {
      case "weighted_response_count":
        return "Response Count";
      // break;
      case "market_size":
        return `${snakeToTitleCase(unit)} (Millions)`;
      // break;
      default:
        return snakeToTitleCase(unit);
      // break;
    }
  } catch (error) {
    console.log(error);
    return unit;
  }
};

const formatMetricValue = (value: any, unit: any): CellObject | any => {
  try {
    value = isNaN(value) ? value : parseFloat(value);

    // Map JS types to Excel types
    const typeMap: Record<string, ExcelDataType> = {
      number: "n",
      string: "s",
      boolean: "b",
    };

    const jsType = typeof value;
    const excelType: ExcelDataType = typeMap[jsType] || "s"; // fallback to 's'

    const cell: CellObject = {
      v: value,
      t: excelType,
    };

    if (unit === "percentage" && excelType === "n") {
      cell.z = "0.0000%";
    }

    if (unit === "market_size" && typeof cell.v === "number") {
      cell.v = parseFloat(cell.v.toFixed(4));
    }

    return cell;
  } catch (err) {
    console.log(err);
    return value;
  }
};
// End of Supporting Functions to export XLSX of an Insight Report
